import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useVenue } from "../VenueProvider";
import { Section, formatMXN, CustomTooltip, ACCENT } from "../cuentas/shared";
import { Kpi, fmtPct, fmtEje, ultimosMeses, labelMes, useDepositos } from "./shared";
import { variacion, cajaPorMetodo, SIN_METODO } from "../../utils/reportes";
import { getMonthRange } from "../../utils/dates";
import {
  CreditCardIcon,
  TableCellsIcon,
  CalendarDaysIcon,
} from "@heroicons/react/20/solid";

// "¿Cuánto entró por transferencia y cuánto en efectivo?" — la caja del
// período agrupada por método. Mismos movimientos que Flujo de caja, así
// que el neto total cuadra con el flujo neto del período.
//
// La gráfica muestra los cuatro métodos con más ingresos del año y junta el
// resto en "Otros": más de cinco colores apilados ya no se leen.
const COLORES_METODO = [ACCENT, "#3B82F6", "#F59E0B", "#8B5CF6"];
const COLOR_OTROS = "#A1A1AA";
const MAX_SERIES = COLORES_METODO.length;

const ReporteMetodosPago = ({ rango, depositos: depositosProp = null }) => {
  const {
    payments,
    refunds,
    costos,
    retiros,
    metodosPagoConDefaultsLista: catalogo,
    calendarTimezone: tz,
  } = useVenue();
  const depositosCargados = useDepositos(!depositosProp);
  const depositos = depositosProp || depositosCargados;

  const movs = useMemo(
    () => ({ payments, refunds, costos, depositos, retiros, catalogo, timeZone: tz }),
    [payments, refunds, costos, depositos, retiros, catalogo, tz]
  );
  const actual = useMemo(
    () => cajaPorMetodo({ ...movs, desde: rango.from, hasta: rango.to }),
    [movs, rango]
  );
  const previo = useMemo(
    () => cajaPorMetodo({ ...movs, desde: rango.prev.from, hasta: rango.prev.to }),
    [movs, rango]
  );
  const previoPorKey = useMemo(() => {
    const m = {};
    previo.metodos.forEach((f) => {
      m[f.key] = f;
    });
    return m;
  }, [previo]);

  // Doce meses de ingresos por método, sin acotar al período: la mezcla se
  // lee en su tendencia.
  const { serie, series } = useMemo(() => {
    const meses = ultimosMeses(12).map((key) => {
      const { from, to } = getMonthRange(key);
      return { key, r: cajaPorMetodo({ ...movs, desde: from, hasta: to }) };
    });
    const totales = {};
    meses.forEach(({ r }) =>
      r.metodos.forEach((f) => {
        if (f.ingresos <= 0) return;
        if (!totales[f.key]) totales[f.key] = { key: f.key, nombre: f.nombre, total: 0 };
        totales[f.key].total += f.ingresos;
      })
    );
    const principales = Object.values(totales)
      .filter((t) => t.key !== SIN_METODO)
      .sort((a, b) => b.total - a.total)
      .slice(0, MAX_SERIES);
    const visibles = new Set(principales.map((t) => t.key));
    const hayOtros = Object.keys(totales).some((k) => !visibles.has(k));
    return {
      series: [
        ...principales.map((t, i) => ({ nombre: t.nombre, color: COLORES_METODO[i] })),
        ...(hayOtros ? [{ nombre: "Otros", color: COLOR_OTROS }] : []),
      ],
      serie: meses.map(({ key, r }) => {
        const fila = { mes: labelMes(key) };
        principales.forEach((t) => {
          fila[t.nombre] = 0;
        });
        if (hayOtros) fila.Otros = 0;
        r.metodos.forEach((f) => {
          if (f.ingresos <= 0) return;
          const destino = visibles.has(f.key) ? totales[f.key].nombre : "Otros";
          fila[destino] += f.ingresos;
        });
        return fila;
      }),
    };
  }, [movs]);

  const { metodos, totales, sinMetodo } = actual;
  const conIngresos = metodos.filter((f) => f.ingresos > 0 && f.key !== SIN_METODO);
  // Columnas que solo existen si el período las usa: la mayoría de los
  // venues no maneja depósitos ni retiros, y dos columnas en cero estorban.
  const conDepositos = metodos.some((f) => Math.abs(f.depositos) > 0.01);
  const conRetiros = metodos.some((f) => f.retiros > 0.01);
  const sinMetodoCount = sinMetodo?.movimientos || 0;

  if (metodos.length === 0) {
    return (
      <Section icon={CreditCardIcon} title="Métodos de pago">
        <p className="text-sm text-zinc-400">
          Sin movimientos de dinero en este período.
        </p>
      </Section>
    );
  }

  const celda = (v, clase = "text-zinc-600 dark:text-zinc-300") => (
    <span className={Math.abs(v) > 0.01 ? clase : "text-zinc-300 dark:text-zinc-600"}>
      {Math.abs(v) > 0.01 ? formatMXN(v) : "—"}
    </span>
  );

  return (
    <div className="space-y-6">
      {conIngresos.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {conIngresos.slice(0, 4).map((f) => (
            <Kpi
              key={f.key}
              label={f.nombre}
              value={formatMXN(f.ingresos)}
              delta={variacion(f.ingresos, previoPorKey[f.key]?.ingresos)}
              hint={`${fmtPct(f.participacion)} de los ingresos · ${f.pagosCount} ${
                f.pagosCount === 1 ? "pago" : "pagos"
              }`}
            />
          ))}
        </div>
      )}

      <Section icon={TableCellsIcon} title="Caja por método">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-100 dark:border-zinc-700/40">
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">Método</th>
                <th className="px-3 py-2 text-right font-semibold text-zinc-600">Ingresos</th>
                <th className="px-3 py-2 text-right font-semibold text-zinc-600">Reembolsos</th>
                <th className="px-3 py-2 text-right font-semibold text-zinc-600">Egresos</th>
                {conDepositos && (
                  <th className="px-3 py-2 text-right font-semibold text-zinc-600">Depósitos</th>
                )}
                {conRetiros && (
                  <th className="px-3 py-2 text-right font-semibold text-zinc-600">Retiros</th>
                )}
                <th className="px-3 py-2 text-right font-semibold text-zinc-600">Neto</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
              {metodos.map((f) => (
                <tr key={f.key}>
                  <td
                    className={`px-3 py-2 whitespace-nowrap ${
                      f.key === SIN_METODO ? "text-amber-600" : "text-zinc-900 dark:text-white font-medium"
                    }`}
                  >
                    {f.nombre}
                    {f.participacion !== null && f.ingresos > 0 && (
                      <span className="ml-1.5 text-xs font-normal text-zinc-400">
                        {Math.round(f.participacion * 100)}%
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{celda(f.ingresos)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{celda(f.reembolsos, "text-red-600")}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{celda(f.egresos, "text-red-600")}</td>
                  {conDepositos && (
                    <td className="px-3 py-2 text-right whitespace-nowrap">{celda(f.depositos)}</td>
                  )}
                  {conRetiros && (
                    <td className="px-3 py-2 text-right whitespace-nowrap">{celda(f.retiros, "text-zinc-500")}</td>
                  )}
                  <td
                    className={`px-3 py-2 text-right whitespace-nowrap font-medium ${
                      f.neto < 0 ? "text-red-600" : "text-emerald-600"
                    }`}
                  >
                    {formatMXN(f.neto)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-zinc-200 dark:border-zinc-700 font-semibold">
                <td className="px-3 py-2 text-zinc-900 dark:text-white">Total</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">{celda(totales.ingresos, "text-zinc-900 dark:text-white")}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">{celda(totales.reembolsos, "text-red-600")}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">{celda(totales.egresos, "text-red-600")}</td>
                {conDepositos && (
                  <td className="px-3 py-2 text-right whitespace-nowrap">{celda(totales.depositos, "text-zinc-900 dark:text-white")}</td>
                )}
                {conRetiros && (
                  <td className="px-3 py-2 text-right whitespace-nowrap">{celda(totales.retiros, "text-zinc-500")}</td>
                )}
                <td
                  className={`px-3 py-2 text-right whitespace-nowrap ${
                    totales.neto < 0 ? "text-red-600" : "text-emerald-600"
                  }`}
                >
                  {formatMXN(totales.neto)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="mt-3 text-xs text-zinc-400">
          El neto es lo que movió cada vía en el período: lo que entró menos lo
          que salió por ella. El total cuadra con el flujo neto de Flujo de caja.
          {conDepositos && " Depósitos es lo recibido menos lo devuelto; aplicarlos no mueve dinero."}
        </p>
        {sinMetodoCount > 0 && (
          <p className="mt-2 text-xs text-amber-600">
            {sinMetodoCount} {sinMetodoCount === 1 ? "movimiento no tiene" : "movimientos no tienen"} método
            capturado. Asígnalo desde el evento o desde Cuentas para que cuente en su vía.
          </p>
        )}
      </Section>

      {series.length > 0 && (
        <Section icon={CalendarDaysIcon} title="Ingresos por método, mes a mes">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={serie} margin={{ top: 10, right: 12, left: -4, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-zinc-200 dark:stroke-zinc-700" />
              <XAxis dataKey="mes" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtEje} tickLine={false} axisLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" iconSize={8} />
              {series.map((s, i) => (
                <Bar
                  key={s.nombre}
                  dataKey={s.nombre}
                  stackId="a"
                  fill={s.color}
                  radius={i === series.length - 1 ? [4, 4, 0, 0] : undefined}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
          <p className="mt-2 text-xs text-zinc-400">
            Los doce meses no se acotan al período de arriba: la mezcla de
            métodos se lee en su tendencia.
          </p>
        </Section>
      )}
    </div>
  );
};

export default ReporteMetodosPago;
